import * as React from "react";
import { Megaphone } from "lucide-react";
import { Button, Skeleton } from "@/components/ui";
import { HoloBorder } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { PostabilityReason } from "@/services/bedrock/dtos/Review";
import { postabilityDisplay, primaryPostabilityReason } from "./postability";

/** "unassigned" is coach-only: the review isn't linked to a player. */
export type ShareCardReason = PostabilityReason | "unassigned";

export interface ShareOnSocialCardProps {
  variant: "ready" | "muted";
  /** Every reason the review can't be posted; the card shows the one that matters most. */
  reasons?: ShareCardReason[];
  /** For the consent line ("Maria didn't give permission…"). */
  reviewerName?: string | null;
  mode?: "player" | "coach";
  playerName?: string;
  onShare: () => void;
  className?: string;
}

const READY_BADGES = ["Attract New Clients", "Branded For You"];

export const ShareOnSocialCard: React.FC<ShareOnSocialCardProps> = ({
  variant,
  reasons,
  reviewerName,
  onShare,
  className,
}) => {
  if (variant === "muted") {
    const postabilityReasons = (reasons ?? []).filter((r): r is PostabilityReason => r !== "unassigned");
    const reason = primaryPostabilityReason(postabilityReasons);
    const display = reason ? postabilityDisplay(reason, reviewerName) : null;
    const Icon = display?.icon;
    return (
      <div
        aria-disabled="true"
        data-testid="share-on-social-card"
        className={cn("flex flex-col p-6 md:p-8 border border-slate-200 bg-slate-50/60 rounded-2xl", className)}
      >
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="p-3 rounded-2xl border border-slate-200 bg-slate-100 text-slate-400 shrink-0 shadow-inner hidden sm:flex">
              <Icon className="h-6 w-6" data-testid={`muted-icon-${display.reason}`} />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className="font-bold text-lg text-slate-700 tracking-tight">
              {display?.title ?? "Post This Review"}
            </div>
            {display && <p className="text-sm text-slate-500 font-medium">{display.message}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <HoloBorder radius="2xl" thickness={2} active={false} hoverActive className={className}>
      <div
        role="button"
        tabIndex={-1}
        onClick={onShare}
        data-testid="share-on-social-card"
        className="flex flex-col p-6 md:p-8 rounded-2xl relative bg-white cursor-pointer"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl border border-indigo-200 bg-indigo-100 text-primary shrink-0 shadow-inner hidden sm:flex">
              <Megaphone className="h-6 w-6" data-testid="ready-icon" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="font-bold text-lg text-slate-900 tracking-tight">Post This Review</div>
              <div className="flex flex-wrap gap-2">
                {READY_BADGES.map(badge => (
                  <div
                    key={badge}
                    className="bg-primary/5 text-primary border border-primary/10 px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider"
                  >
                    {badge}
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500 font-medium mt-3">
                Takes <span className="font-bold text-slate-700">under a minute</span>. Just pick a design and post.
              </p>
            </div>
          </div>
          <Button
            variant="default"
            className="w-full md:w-auto h-10 font-bold px-6"
            onClick={event => {
              // The card is clickable too.
              event.stopPropagation();
              onShare();
            }}
          >
            Share on social
          </Button>
        </div>
      </div>
    </HoloBorder>
  );
};

/** The ready card's shape while the review panel works out what to show. */
export const ShareOnSocialCardSkeleton: React.FC = () => (
  <div
    className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
    aria-hidden="true"
    data-testid="share-card-skeleton"
  >
    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
      <div className="flex items-start gap-4">
        <Skeleton className="hidden size-12 shrink-0 rounded-2xl sm:flex" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-40" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-32 rounded-md" />
            <Skeleton className="h-5 w-28 rounded-md" />
          </div>
          <Skeleton className="mt-2 h-4 w-60" />
        </div>
      </div>
      <Skeleton className="h-10 w-full rounded-lg md:w-36" />
    </div>
  </div>
);
